import { useId, type MouseEvent, type ReactNode } from 'react'
import { Button } from './Button'
import { useOverlay } from './use-overlay'

export type DialogProps = {
  open: boolean
  title: string
  description?: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  className?: string
}

export function Dialog({ open, title, description, onClose, children, footer, className = '' }: DialogProps) {
  const titleId = useId()
  const descriptionId = useId()
  const dialogRef = useOverlay<HTMLElement>(open, onClose)
  if (!open) return null

  const closeFromBackdrop = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose()
  }
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-[var(--color-overlay)] backdrop-blur-sm sm:items-center sm:p-7" onMouseDown={closeFromBackdrop}>
      <section
        ref={dialogRef}
        className={`max-h-[92dvh] w-full overflow-auto rounded-t-[var(--radius-lg)] bg-[var(--color-panel)] shadow-[var(--shadow-lg)] sm:max-h-[calc(100dvh-56px)] sm:rounded-[var(--radius-lg)] ${/(?:^|\s)max-w-/.test(className) ? '' : 'max-w-2xl'} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
      >
        <div className="flex items-start justify-between gap-4 border-b border-[var(--color-line)] px-5 py-4">
          <div>
            <h2 id={titleId} className="m-0 text-lg font-semibold text-[var(--color-ink)]">{title}</h2>
            {description ? <p id={descriptionId} className="m-0 mt-1 text-xs text-[var(--color-muted)]">{description}</p> : null}
          </div>
          <Button variant="icon" onClick={onClose} aria-label="关闭对话框">×</Button>
        </div>
        {children}
        {footer ? <div className="flex items-center justify-end gap-2 border-t border-[var(--color-line)] px-5 py-3">{footer}</div> : null}
      </section>
    </div>
  )
}

export function DialogBody({ children }: { children: ReactNode }) {
  return <div className="px-5 py-5">{children}</div>
}

export function DialogActions({ children }: { children: ReactNode }) {
  return <div className="mt-5 flex justify-end gap-2">{children}</div>
}
